import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

interface AppConfig {
  apiKey: string
  chatEnabled: boolean
  connectRegion: string
}

interface ConfigContextValue {
  config: AppConfig | null
  loading: boolean
  error: string | null
}

const ConfigContext = createContext<ConfigContextValue>({
  config: null,
  loading: true,
  error: null,
})

/**
 * Fetches runtime configuration from the /api/config endpoint.
 * This eliminates the need for build-time VITE_* environment variables.
 */
async function fetchConfig(): Promise<AppConfig> {
  const response = await fetch('/api/config')
  if (!response.ok) {
    throw new Error(`Failed to fetch config: ${response.status}`)
  }
  return response.json()
}

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchConfig()
      .then(setConfig)
      .catch(err => {
        console.error('Failed to load config:', err)
        setError(err.message)
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <ConfigContext.Provider value={{ config, loading, error }}>
      {children}
    </ConfigContext.Provider>
  )
}

/**
 * Hook to access the runtime configuration.
 * Returns { config, loading, error } where config is null until loaded.
 */
export function useConfig(): ConfigContextValue {
  return useContext(ConfigContext)
}
